import { ref, reactive, type Ref } from "vue";
import { io, type Socket } from "socket.io-client";
import type { FeatureFlag, Environment, FeatureFlagConfig } from "../types";
import { DEFAULT_CONFIG } from "../config/config";
import { ENVIRONMENTS } from "../types";

export class FeatureFlagService {
  private socket: Socket | null = null;
  private activeEnvironments: Environment[];

  // Reactive state
  public flags: Ref<Record<string, FeatureFlag>> = ref({});
  public isLoading: Ref<boolean> = ref(true);
  public error: Ref<Error | null> = ref(null);

  constructor(private config: FeatureFlagConfig) {
    this.activeEnvironments = this.config.environments || [...ENVIRONMENTS];
    this.initialize();
  }

  private async initialize(): Promise<void> {
    await this.fetchFlags();
    this.initializeWebSocket();
  }

  private async fetchFlags(): Promise<void> {
    try {
      const response = await fetch(
        `${DEFAULT_CONFIG.API_URL}/api/feature-flags/sdk`,
        {
          headers: {
            "x-api-key": this.config.apiKey,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const flagsArray: FeatureFlag[] = await response.json();

      const flagsMap = flagsArray.reduce((acc, flag) => {
        if (
          !flag.environments?.length ||
          flag.environments.some((env) => this.activeEnvironments.includes(env))
        ) {
          acc[flag.name] = flag;
        }
        return acc;
      }, {} as Record<string, FeatureFlag>);

      this.flags.value = flagsMap;
      this.error.value = null;
    } catch (err) {
      console.error("[FlagPole SDK] Error fetching flags:", err);
      this.error.value =
        err instanceof Error ? err : new Error("Failed to fetch flags");
    } finally {
      this.isLoading.value = false;
    }
  }

  private initializeWebSocket(): void {
    const wsUrl = DEFAULT_CONFIG.WS_URL;
    console.log("[FlagPole SDK] WS URL before initialization:", wsUrl);

    this.socket = io(wsUrl, {
      auth: { apiKey: this.config.apiKey },
      query: {
        environments: this.activeEnvironments.join(","),
      },
      forceNew: true,
      path: "/socket.io",
      transports: ["websocket", "polling"],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 3,
    });

    this.socket.on("connect", () => {
      console.log("[FlagPole SDK] Connected to WebSocket");
      this.error.value = null;
    });

    this.socket.on("connect_error", (err) => {
      console.error("[FlagPole SDK] Failed to connect:", err);
      this.error.value =
        err instanceof Error ? err : new Error("WebSocket connection failed");
    });

    this.socket.on("featureFlagUpdate", (updatedFlag: FeatureFlag) => {
      if (
        !updatedFlag.environments?.length ||
        updatedFlag.environments.some((env) =>
          this.activeEnvironments.includes(env)
        )
      ) {
        this.flags.value = {
          ...this.flags.value,
          [updatedFlag.name]: updatedFlag,
        };
      }
    });

    this.socket.on("featureFlagDelete", (flagId: string) => {
      const currentFlags = { ...this.flags.value };
      const flagName = Object.keys(currentFlags).find(
        (name) => currentFlags[name]._id === flagId
      );
      if (flagName) {
        delete currentFlags[flagName];
        this.flags.value = currentFlags;
      }
    });
  }

  public isFeatureEnabled(flagName: string): boolean {
    const flag = this.flags.value[flagName];

    if (!flag) {
      console.warn(`[FlagPole SDK] Flag "${flagName}" not found`);
      return false;
    }

    return (
      flag.isEnabled &&
      (!flag.environments?.length ||
        flag.environments.some((env) => this.activeEnvironments.includes(env)))
    );
  }

  public getFlag(flagName: string): FeatureFlag | null {
    return this.flags.value[flagName] || null;
  }

  public getAllFlags(): Record<string, FeatureFlag> {
    return this.flags.value;
  }

  public destroy(): void {
    if (this.socket) {
      this.socket.disconnect();
    }
  }
}
